/**
 * Row labels and the Apply-all filter for the Pending changes tray
 * (PendingTray.tsx). A plain module so pendingTrayHeld.test.ts can call them
 * and so the tray file keeps exporting only its component
 * (react-refresh/only-export-components).
 *
 * Held rows (PR 16 plan P10, PR 17): an Image studio listing image update
 * ('listing_images') and a Launch tab listing draft ('listing') are sent by the
 * listing sender, which the server keeps switched off until an admin turns it
 * on (GET /api/launches/status `listing_writes`). While it is off the server
 * refuses them with a 409, so the tray shows a chip instead of an Apply button
 * and leaves them out of Apply all. When it is on they apply like any row.
 *
 * Staged harvests (PR 5b): a `harvest` row waits behind two server switches
 * (harvest sending; for a new campaign also campaign creation), read from
 * GET /api/manual-edits/live-writes-status. While one is off the row shows a
 * chip instead of Apply, and Apply all leaves it out; a new-campaign harvest
 * is also never part of Apply all. PR 24: those are created together by the
 * tray's "Create all N campaigns" (creatableIds), after one confirm that lists
 * every campaign (campaignConfirmRows / campaignTotals).
 */
import type { LiveWritesStatusResponse, ManualEdit, NewCampaignResult } from '../lib/api'
import { isHeldTrayRow as isHeldListingRow } from '../lib/imageStudio'
import { fmtMoney } from '../lib/format'
import { changeHeadline, entityTypeLabel, placementLabel } from '../lib/labels'

/** The neutral chip a held row shows where Apply would be. */
export const HELD_ROW_CHIP = 'Listing updates are switched off on this server'

/** The switches that hold harvest rows, from the live-writes-status payload. */
export type TraySwitches = Partial<Pick<LiveWritesStatusResponse, 'harvest_writes_enabled' | 'campaign_creation_enabled'>> & {
  /** GET /api/launches/status `listing_writes`. Unknown counts as off. */
  listing_writes?: boolean
}

type RowKind = Pick<ManualEdit, 'field'> & Partial<Pick<ManualEdit, 'entity_type' | 'context'>>

/** The chips a harvest row shows while a switch is off. */
export const HARVEST_HELD_CHIP = 'Waits for harvest sending to be switched on'
export const HARVEST_CREATION_HELD_CHIP = 'Waits for campaign creation to be switched on'

const isHarvest = (it: RowKind) => it.entity_type === 'harvest' || it.field === 'harvest'
const isNewCampaignHarvest = (it: RowKind) => isHarvest(it) && it.context?.dest_kind === 'new_campaign'

/** The chip a held row shows where Apply would be, or null when it can be
 *  sent. `writes` is null while the status is unknown: nothing is claimed
 *  held then (the server re-checks on every Apply and says so). */
export function heldRowChip(it: RowKind, writes?: TraySwitches | null): string | null {
  if (isHeldListingRow(it, writes?.listing_writes === true)) return HELD_ROW_CHIP
  if (isHarvest(it) && writes) {
    if (writes.harvest_writes_enabled === false) return HARVEST_HELD_CHIP
    if (isNewCampaignHarvest(it) && writes.campaign_creation_enabled === false) return HARVEST_CREATION_HELD_CHIP
  }
  return null
}

/** A row ClickPay cannot send right now: a listing or listing image update
 *  while listing updates are off, or a harvest whose switch is off. */
export function isHeldTrayRow(it: RowKind, writes?: TraySwitches | null): boolean {
  return heldRowChip(it, writes) !== null
}

/** Entity-type-aware label for the raw id line (Target ID / Keyword ID / …). */
export function entityIdLabel(t: string): string {
  switch (t) {
    case 'target':   return 'Target ID'
    case 'keyword':  return 'Keyword ID'
    case 'campaign': return 'Campaign ID'
    case 'placement': return 'Placement'
    case 'listing_images': return 'Listing images'
    case 'harvest': return 'Harvest'
    case 'listing': return 'Listing'
    default:         return `${t.charAt(0).toUpperCase()}${t.slice(1)} ID`
  }
}

/** The id line's value: a placement row is filed as '<campaign>:<bucket>', and the
 *  bucket prints in words ('C1:rest_of' -> 'C1:Rest of search'). Every other id as-is. */
export function entityIdText(t: string, id: string): string {
  const i = id.indexOf(':')
  if (t !== 'placement' || i < 0) return id
  return `${id.slice(0, i)}:${placementLabel(id.slice(i + 1))}`
}

/** A row's headline: "entity · field", except a listing or listing image
 *  update, which reads as its own label ("PB086 · main, 03", "PB404 · new
 *  listing PB_MAGCHESS_LG") and a harvest, which reads as
 *  the term ("Harvest · “kids scale”"), rather than internal names. Every
 *  other row is "Keyword · Bid" (lib/labels.ts), never the raw codes. */
export function trayHeadline(it: Pick<ManualEdit, 'entity_type' | 'field' | 'context'>): string {
  if (it.entity_type === 'listing_images') return it.context?.label || 'Listing images'
  if (it.entity_type === 'listing') return it.context?.label || 'Listing'
  if (it.entity_type === 'harvest') return it.context?.label ? `Harvest · “${it.context.label}”` : 'Harvest'
  // A new campaign reads as its name, not "campaign_launch · launch_campaign" (E1).
  if (it.entity_type === 'campaign_launch') return `${entityTypeLabel('campaign_launch')} · ${it.context?.campaign_name || 'launch'}`
  return changeHeadline(it.entity_type, it.field)
}

/** The plain lines a harvest row adds under its headline: where the term came
 *  from, where it goes, and whether it is blocked at the source. */
export function harvestRowLines(context: ManualEdit['context']): string[] {
  if (!context) return []
  const out: string[] = []
  const place = (camp?: string | null, ag?: string | null) => [camp, ag].filter(Boolean).join(' › ')
  const from = place(context.source_campaign_name, context.source_ad_group_name)
  if (from) out.push(`From ${from}`)
  const to = place(context.campaign_name, context.dest_kind === 'new_campaign' ? null : context.ad_group_name)
  if (to) out.push(`${context.dest_kind === 'new_campaign' ? 'Into a new campaign' : 'Into'} ${to}`)
  if (context.negate_source) out.push('Blocks it where it came from')
  return out
}

/** The ids Apply all may push: every pending row except the held ones and the
 *  new-campaign harvests (a campaign is created on its own, with its row's
 *  Apply). */
export function sendableIds(
  items: ReadonlyArray<Pick<ManualEdit, 'id' | 'field'> & Partial<Pick<ManualEdit, 'entity_type' | 'context'>>>,
  writes?: TraySwitches | null,
): number[] {
  return items.filter(i => !isHeldTrayRow(i, writes) && !isNewCampaignHarvest(i)).map(i => i.id)
}

/** The ids "Create all" may create: new-campaign harvests that are not held by
 *  a switch, in list order (the order they are created in). */
export function creatableIds(
  items: ReadonlyArray<Pick<ManualEdit, 'id' | 'field'> & Partial<Pick<ManualEdit, 'entity_type' | 'context'>>>,
  writes?: TraySwitches | null,
): number[] {
  return items.filter(i => isNewCampaignHarvest(i) && !isHeldTrayRow(i, writes)).map(i => i.id)
}

/** The ids the tray's "Create all N campaigns" button creates: the creatable
 *  rows when there are two or more AND harvest sending and campaign creation
 *  are known to be on, else none. One row keeps its own Apply; an unknown
 *  switch state offers nothing (the server would refuse a run it cannot send). */
export function createAllIds(
  items: ReadonlyArray<Pick<ManualEdit, 'id' | 'field'> & Partial<Pick<ManualEdit, 'entity_type' | 'context'>>>,
  writes?: TraySwitches | null,
): number[] {
  if (writes?.harvest_writes_enabled !== true || writes.campaign_creation_enabled !== true) return []
  const ids = creatableIds(items, writes)
  return ids.length >= 2 ? ids : []
}

/** The house hard cap on a bid (launch_campaign.BID_MAX): a row at it was
 *  capped, not chosen, so the confirm warns on it. */
export const HOUSE_MAX_BID = 10

/** A new campaign's bid is held to this share of its daily budget
 *  (harvest_bid.NEW_CAMPAIGN_BID_BUDGET_FRACTION). */
export const BID_BUDGET_FRACTION = 0.2

/** One campaign in the "Create all" confirm. */
export type CampaignConfirmRow = {
  id: number
  name: string
  term: string
  match: string
  bid: number | null
  dailyBudget: number | null
  portfolio: string | null
  /** The term is also blocked in its source ad group. */
  negate: boolean
  /** The bid sits at the $10 house maximum (an older staging that was capped silently). */
  atHouseMax: boolean
  /** A house limit lowered the bid when it was staged ('house_max' | 'budget'). */
  limited: 'house_max' | 'budget' | null
  /** What the formula gave before the limit, when known. */
  formula: number | null
  /** Staged before the budget limit existed: the bid is above the share of the
   *  daily budget a new row would be held to (not set when a limit already applied). */
  aboveBudgetShare?: boolean
}

/** The confirm's rows for `ids`, in the order given. */
export function campaignConfirmRows(
  items: ReadonlyArray<Pick<ManualEdit, 'id'> & Partial<Pick<ManualEdit, 'context'>>>,
  ids: readonly number[],
): CampaignConfirmRow[] {
  const byId = new Map(items.map(i => [i.id, i]))
  const rows: CampaignConfirmRow[] = []
  for (const id of ids) {
    const it = byId.get(id)
    if (!it) continue
    const c = it.context ?? {}
    const bid = typeof c.bid === 'number' ? c.bid : null
    rows.push({
      id,
      name: c.campaign_name || `edit ${id}`,
      term: c.label || '',
      match: (c.match_type || 'EXACT').toUpperCase(),
      bid,
      dailyBudget: typeof c.daily_budget === 'number' ? c.daily_budget : null,
      portfolio: c.portfolio_name || c.portfolio_id || null,
      negate: !!c.negate_source,
      atHouseMax: bid != null && bid >= HOUSE_MAX_BID,
      limited: c.bid_limited ?? null,
      formula: typeof c.bid_formula === 'number' ? c.bid_formula : null,
      aboveBudgetShare: !c.bid_limited && bid != null
        && typeof c.daily_budget === 'number' && c.daily_budget > 0
        && bid > c.daily_budget * BID_BUDGET_FRACTION + 1e-9,
    })
  }
  return rows
}

/** The warning under a campaign whose bid a house limit touched, or null. */
export function bidWarning(r: Pick<CampaignConfirmRow, 'atHouseMax' | 'limited' | 'formula'> & { aboveBudgetShare?: boolean; bid?: number | null; dailyBudget?: number | null }): string | null {
  if (r.limited) {
    return r.formula != null ? `bid lowered from ${fmtMoney(r.formula)}` : 'bid lowered by a house limit'
  }
  if (r.atHouseMax) {
    return `bid at the ${fmtMoney(HOUSE_MAX_BID)} house maximum — it was capped, not chosen`
      + (r.formula != null ? `; the formula gave ${fmtMoney(r.formula)}` : '')
  }
  if (r.aboveBudgetShare && r.bid != null && r.dailyBudget != null) {
    return `bid ${fmtMoney(r.bid)} is above a fifth of the ${fmtMoney(r.dailyBudget)}/day budget — staged before that limit existed`
  }
  return null
}

/** How many campaigns, and the daily budget they add up to. A row with no
 *  budget on record adds nothing (`unknown` counts them, so the total is not
 *  claimed as complete). */
export function campaignTotals(rows: ReadonlyArray<Pick<CampaignConfirmRow, 'dailyBudget'>>): {
  count: number; dailyBudget: number; unknown: number
} {
  let dailyBudget = 0, unknown = 0
  for (const r of rows) {
    if (r.dailyBudget == null) unknown++
    else dailyBudget += r.dailyBudget
  }
  return { count: rows.length, dailyBudget: Math.round(dailyBudget * 100) / 100, unknown }
}

/** The toast line while a run is going: `done` rows are finished, so the one
 *  in flight is done + 1. */
export function creatingToastText(done: number, total: number, label?: string | null): string {
  const n = Math.min(done + 1, total)
  return `Creating ${n} of ${total}${label ? ` — ${label}` : ''}`
}

/** A finished run in the buckets the batch path uses. A `refused` row (a guard
 *  or a claim said no before anything was sent) is a failure with its reason. */
export function jobOutcome(results: ReadonlyArray<NewCampaignResult>): {
  applied: number; staged: number
  skipped: { id: number; error: string | null }[]
  failed: { id: number; error: string }[]
} {
  let applied = 0, staged = 0
  const skipped: { id: number; error: string | null }[] = []
  const failed: { id: number; error: string }[] = []
  for (const r of results) {
    const b = outcomeBucket(r.status)
    if (b === 'applied') applied++
    else if (b === 'staged') staged++
    else if (b === 'skipped') skipped.push({ id: r.id, error: r.error })
    else failed.push({ id: r.id, error: r.error || 'the campaign was not created' })
  }
  return { applied, staged, skipped, failed }
}

/** Plain-English reason text for rows Apply all leaves out, by reason (new
 *  campaigns are created with "Create all"; a switch-held harvest waits; a
 *  listing update is held while listing updates are off). Null when nothing is held. */
export function heldSummary(
  items: ReadonlyArray<RowKind & { id: number }>,
  writes?: TraySwitches | null,
): string | null {
  let newCampaigns = 0, harvestWaiting = 0, images = 0
  for (const it of items) {
    if (isHeldListingRow(it, writes?.listing_writes === true)) images++
    else if (isHarvest(it) && writes && heldRowChip(it, writes) !== null) harvestWaiting++
    else if (isNewCampaignHarvest(it)) newCampaigns++
  }
  const parts: string[] = []
  // "Create all" needs two or more; a lone new campaign is created with its own Apply.
  if (newCampaigns === 1) parts.push('1 new campaign is created with its own Apply')
  else if (newCampaigns > 1) parts.push(`${newCampaigns} new campaigns are created with “Create all”`)
  if (harvestWaiting) parts.push(`${harvestWaiting} harvest${harvestWaiting === 1 ? ' waits' : 's wait'} for sending to be switched on`)
  if (images) parts.push(`${images} listing update${images === 1 ? '' : 's'} can't be sent while listing updates are switched off`)
  return parts.length ? `${parts.join('; ')}. They stay queued.` : null
}

/** A failed harvest cannot be re-staged from the tray (it is staged only by the
 *  Harvest dialog), so the tray offers no Retry for it. */
export function canRetryFailed(it: RowKind): boolean {
  return !isHarvest(it)
}

/** How an Apply outcome is shown: a guard-skipped row was retired on purpose
 *  (nothing was sent), which is not a failure. */
export function outcomeBucket(status: string): 'applied' | 'staged' | 'skipped' | 'failed' {
  if (status === 'applied') return 'applied'
  if (status === 'staged_no_writes') return 'staged'
  if (status === 'skipped') return 'skipped'
  return 'failed'
}

/** Whether this operator's tray should adopt a run the server reports as
 *  active: only their own (an older server sends no operator: adopt it). */
export function ownsRun(job: { operator?: string | null }, activeOperator: string): boolean {
  return !job.operator || job.operator === activeOperator
}

/** A "Create all" start the server turned away before claiming anything
 *  (switch off, a run already going, no row still pending): 409 / 422.
 *  Returns the plain message to show, or null when it was some other error. */
export function createRefusalMessage(e: unknown): string | null {
  const err = e as { status?: unknown; serverMessage?: unknown; body?: unknown } | null
  if (!err || (err.status !== 409 && err.status !== 422)) return null
  const raw = typeof err.serverMessage === 'string' && err.serverMessage ? err.serverMessage : null
  let msg = raw
  if (!msg) {
    const d = (err.body as { detail?: { errors?: unknown } } | null)?.detail
    const first = Array.isArray(d?.errors) ? d?.errors[0] : null
    msg = typeof first === 'string' ? first : null
  }
  // Never show an environment variable name on screen.
  msg = (msg ?? 'The server turned the request away').replace(/\s*\(?[A-Z][A-Z0-9]*_[A-Z0-9_]+\)?/g, '').trim()
  return `Nothing was sent; the campaigns stay queued. ${msg}`
}
